import { isLocationPR, isPathBased, isLocationContentPlatform } from '../enums/ops-console-apps';
/**
 * Returns the Okta `OktaAuthOptions` used by both the pre-bootstrap auth flow
 * (main.ts → authenticateAndLoadConfigsBeforeBootstrap) and the runtime
 * Angular module (WebAngularSharedDataAccessOktaAuth2Module.forRoot). Single
 * source of truth — keeps redirectUri, postLogoutRedirectUri, restoreOriginalUri,
 * PKCE, and other Okta options consistent between the bootstrap-time and
 * runtime OktaAuth instances.
 *
 * @param _environment - Environment (currently unused, kept for API consistency)
 * @param baseHref - Optional base href for redirect URI calculation
 */
export function getOktaConfigForApp(_environment, baseHref) {
    const baseConfig = {
        // Okta authorization server URL - where authentication requests are sent
        issuer: 'https://cbsi.okta.com',
        clientId: '0oa1i9w6aggywRHwe1d8', // Okta application client ID - identifies this app to Okta
        scopes: ['openid', 'email', 'profile', 'groups'],
        // PKCE (Proof Key for Code Exchange) - OAuth2 security extension required
        // for SPA authorization-code flow.
        pkce: true,
        headers: { 'RelayState': { 'format': 'FROM_URL' } },
        state: 'origin' + window.location.href,
    };
    // ============================================================================
    // REDIRECT URI DETERMINATION
    // ============================================================================
    // The redirect URI is where Okta sends the user after authentication.
    // It must match what's configured in the Okta application settings.
    //
    // Rules:
    // 1. PR environment: Use /pr/login/callback (special path for PR deployments)
    // 2. (Localhost OR isPathBased()) AND NOT isLocationContentPlatform(): Use /login/callback
    //    - isPathBased() = true when on operationsconsole.paramountmsc.com with a path-based app
    //    - These apps share a single Okta callback at /login/callback
    //    - But apps on contentplatform.viacom.com need their own path-based callbacks
    // 3. Otherwise: Use baseHref + /login/callback (app-specific path)
    let redirectUri;
    // Check if this is a PR (Pull Request) environment
    // PR environments use a special callback path: /pr/login/callback
    if (isLocationPR()) {
        redirectUri = window.location.origin + '/pr/login/callback';
    }
    else if ((window.location.origin.includes('localhost') || isPathBased())
        && !isLocationContentPlatform()) {
        redirectUri = window.location.origin + '/login/callback';
    }
    // For all other environments (contentplatform.viacom.com or standalone deployments),
    // append baseHref to the callback path: /fulfillment/login/callback
    else {
        redirectUri = window.location.origin + (baseHref || '') + '/login/callback';
    }
    // Post-logout URI must exactly match one of the "Logout redirect URIs" registered
    // in the Okta app settings. Without an explicit value the SDK falls back to
    // window.location.origin (no trailing slash), which mismatches the registered
    // URI for root-URL apps (e.g. https://dev-vcap.paramountmsc.com/) → Okta 400.
    //
    // Rules (parallel to redirectUri above):
    //   - PR env           → origin + /pr
    //   - Localhost        → origin only (no baseHref; all apps share localhost)
    //   - Path-based apps  → origin + baseHref (e.g. /media-ingest-console)
    //                        Lands user back on their specific app after logout so
    //                        the next bootstrap captures the correct app URL in state,
    //                        fixing the "re-login always goes to root" bug.
    //   - Root/standalone  → origin + baseHref (or '/' when baseHref is empty)
    //                        Empty baseHref (CRMAM, etc.) gets trailing slash via `|| '/'`
    let postLogoutRedirectUri;
    if (isLocationPR()) {
        postLogoutRedirectUri = window.location.origin + '/pr';
    }
    else if (window.location.origin.includes('localhost')) {
        postLogoutRedirectUri = window.location.origin;
    }
    else {
        postLogoutRedirectUri = window.location.origin + (baseHref || '/');
    }
    // ============================================================================
    // RETURN COMPLETE OKTA CONFIGURATION
    // ============================================================================
    // Combine base config with environment-specific redirectUri and restoreOriginalUri callback
    return {
        ...baseConfig,
        // Override redirectUri with the calculated value
        redirectUri,
        postLogoutRedirectUri,
        // Callback function that Okta calls after successful authentication
        // This restores the user to their original destination before login redirect
        restoreOriginalUri: async (oktaAuth, originalUri) => {
            console.log('🚀🚀 -- restoreOriginalUri', oktaAuth, originalUri);
            let previousUrl = oktaAuth?.options?.state?.split('state=origin').pop();
            // Decode the URL if it exists (it may be URL-encoded)
            // Added null check (improvement over original which could throw if previousUrl is undefined)
            previousUrl = previousUrl ? decodeURIComponent(previousUrl) : null;
            // Use the previous URL if available, otherwise fall back to originalUri from Okta
            const redirectUrl = previousUrl || originalUri;
            // Navigate to the final destination
            window.location.href = redirectUrl;
        }
    };
}
//# sourceMappingURL=okta-config-provider.util.js.map